import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import TransactionDetailDrawer from "@/components/TransactionDetailDrawer";
import RetryWorkerBar from "@/components/RetryWorkerBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Sparkles, Trash2, RefreshCw, Search, Eye } from "lucide-react";

const STATUSES = [
  { v: "all", label: "Todos" },
  { v: "created", label: "Creadas" },
  { v: "pending", label: "Pendientes" },
  { v: "failed", label: "Fallidas" },
  { v: "cancelled", label: "Canceladas" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}
function fmtEUR(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

function KpiCard({ label, value, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold tracking-tight ${accent || "text-slate-900"}`} style={{ fontFamily: "Chivo, sans-serif" }}>
        {value}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (e) {
      console.error("Dashboard: failed to load stats", e);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("rental_id", search.trim());
      params.set("limit", "200");
      const { data } = await api.get(`/admin/transactions?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const onSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/demo/seed?count=10");
      toast.success(`Seed OK: ${data.created} transacciones creadas`);
      await Promise.all([loadStats(), loadItems()]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSeeding(false); }
  };

  const onClear = async () => {
    if (!window.confirm("¿Vaciar todas las transacciones y la cola? Esta acción es irreversible.")) return;
    setClearing(true);
    try {
      const { data } = await api.post("/admin/demo/clear");
      toast.success(`Eliminadas ${data.transactions_deleted} transacciones y ${data.pending_deleted} de la cola`);
      await Promise.all([loadStats(), loadItems()]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setClearing(false); }
  };

  const onOpenDetail = (id) => {
    setSelected(id);
    setDrawerOpen(true);
  };

  return (
    <Layout>
      {/* Header bar */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Chivo, sans-serif" }}>
            Dashboard
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Transacciones alquiler → póliza. Idempotencia, reintentos y trazabilidad.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onSeed}
            disabled={seeding}
            data-testid="seed-data-button"
            className="h-9 text-xs"
          >
            <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${seeding ? "animate-pulse" : ""}`} />
            {seeding ? "Generando…" : "Seed demo"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onClear}
            disabled={clearing}
            data-testid="clear-data-button"
            className="h-9 text-xs text-red-700 hover:text-red-800 hover:bg-red-50 border-red-200"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            Vaciar tabla
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <KpiCard testId="kpi-total"     label="Total"      value={stats?.total ?? "—"} />
        <KpiCard testId="kpi-created"   label="Creadas"    value={stats?.created ?? "—"} accent="text-emerald-700" />
        <KpiCard testId="kpi-pending"   label="Pendientes" value={stats?.pending ?? "—"} accent="text-amber-700" />
        <KpiCard testId="kpi-failed"    label="Fallidas"   value={stats?.failed ?? "—"} accent="text-red-700" />
        <KpiCard testId="kpi-cancelled" label="Canceladas" value={stats?.cancelled ?? "—"} accent="text-slate-700" />
        <KpiCard testId="kpi-deadletter" label="Dead-letter" value={stats?.dead_letter ?? "—"} accent="text-red-900" />
        <KpiCard testId="kpi-insured"   label="Asegurado (€)" value={stats ? fmtEUR(stats.insured_eur) : "—"} />
      </div>

      {/* Retry worker bar */}
      <RetryWorkerBar onChanged={() => { loadStats(); loadItems(); }} />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-3 border-b border-slate-200">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              data-testid="search-rental-input"
              placeholder="Buscar por rental_id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 text-sm w-full sm:w-48" data-testid="filter-status-select">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s.v} value={s.v} data-testid={`filter-status-option-${s.v}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs"
            data-testid="refresh-transactions-button"
            onClick={() => { loadStats(); loadItems(); }}
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refrescar
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Rental</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Estado</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Póliza</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Bici</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Valor</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Intentos</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Próximo reintento</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Último error</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider font-bold text-slate-600 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-slate-500 py-8" data-testid="tx-loading">
                    Cargando transacciones…
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-slate-500 py-8" data-testid="tx-empty">
                    No hay transacciones. Pulsa <span className="font-semibold">“Seed demo”</span> para generar datos de ejemplo.
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((tx) => (
                <TableRow key={tx.id} className="hover:bg-slate-50/70" data-testid={`tx-row-${tx.rental_id}`}>
                  <TableCell className="font-mono text-xs">{tx.rental_id}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={tx.status} />
                      {tx.dead_letter && (
                        <span
                          data-testid={`dead-letter-badge-${tx.rental_id}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-red-900 text-white"
                          title="Dead-letter: máximo de reintentos alcanzado"
                        >
                          DL
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-700">{tx.policy_id || "—"}</TableCell>
                  <TableCell className="text-xs">
                    <div className="font-semibold text-slate-900">{tx.raw_input?.bike?.brand} {tx.raw_input?.bike?.model}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{tx.raw_input?.bike?.type}</div>
                  </TableCell>
                  <TableCell className="text-xs font-mono">{fmtEUR(tx.bike_value_eur)}</TableCell>
                  <TableCell className="text-xs font-mono" data-testid={`tx-attempts-${tx.rental_id}`}>
                    <span className={tx.attempts >= 5 ? "text-red-700 font-bold" : ""}>
                      {tx.attempts}
                    </span>
                  </TableCell>
                  <TableCell className="text-[11px] font-mono text-slate-700" data-testid={`tx-next-retry-${tx.rental_id}`}>
                    {tx.dead_letter ? <span className="text-red-700">dead-letter</span> : (tx.next_retry_at ? fmtDate(tx.next_retry_at) : "—")}
                  </TableCell>
                  <TableCell className="text-[11px] text-red-700 max-w-[220px] truncate" title={tx.last_error || ""} data-testid={`tx-last-error-${tx.rental_id}`}>
                    {tx.last_error || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-[11px]"
                      data-testid={`row-view-details-button-${tx.rental_id}`}
                      onClick={() => onOpenDetail(tx.id)}
                    >
                      <Eye className="w-3 h-3 mr-1" /> Ver
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <TransactionDetailDrawer
        transactionId={selected}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onChanged={() => { loadStats(); loadItems(); }}
      />
    </Layout>
  );
}
